import { useEffect, useRef, useCallback } from 'react';

interface UseScannerOptions {
  onScan: (barcode: string) => void;
  minLength?: number;
  maxDelay?: number;
  enabled?: boolean;
}

/**
 * Hook to detect barcode scanner input
 * Scanners typically type very fast (< 50ms between keystrokes)
 * and end with Enter key
 */
export function useBarcodeScanner({
  onScan,
  minLength = 3,
  maxDelay = 50,
  enabled = true,
}: UseScannerOptions) {
  const bufferRef = useRef<string>('');
  const lastKeypressRef = useRef<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!enabled) return;
    
    // Ignore if typing in specific inputs that shouldn't trigger scan
    const target = e.target as HTMLElement;
    if (target.tagName === 'TEXTAREA') return;
    
    const now = Date.now();
    const timeDiff = now - lastKeypressRef.current;
    
    // If too slow between keystrokes, reset buffer
    if (timeDiff > maxDelay && bufferRef.current.length > 0) {
      bufferRef.current = '';
    }
    
    lastKeypressRef.current = now;
    
    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Handle Enter key - submit the scan
    if (e.key === 'Enter' && bufferRef.current.length >= minLength) {
      e.preventDefault();
      onScan(bufferRef.current);
      bufferRef.current = '';
      return;
    }
    
    // Only accept printable characters
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      bufferRef.current += e.key;
      
      // Auto-clear after a delay (in case Enter wasn't pressed)
      timeoutRef.current = setTimeout(() => {
        if (bufferRef.current.length >= minLength) {
          onScan(bufferRef.current);
        }
        bufferRef.current = '';
      }, 100);
    }
  }, [enabled, onScan, minLength, maxDelay]);

  useEffect(() => {
    if (!enabled) return;
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [handleKeyDown, enabled]);

  const reset = useCallback(() => {
    bufferRef.current = '';
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  return { reset };
}
